import {format} from 'date-fns';
import {toast} from 'react-toastify';
import {twMerge} from 'tailwind-merge';
import {observer} from 'mobx-react-lite';
import {ArrowPathIcon} from '@heroicons/react/24/outline';
import React, {FC, useEffect, useRef, useState} from 'react';
import {IWebhookDelivery, TWebhookDeliveryStatus, TWebhookEvent, WEBHOOK_EVENTS} from '@verdocs/js-sdk';
import {Badge, Button, Card, EmptyState, FIELD_CLASSES, LineChart, RowAction, SectionTitle} from '../../../Components';
import {resendDelivery, useWebhookDeliveries, useWebhookDeliveryStats} from '../../../react-sdk/queries';
import {SegmentedControl, Spinner, StatTile} from '../../../Components';
import {DELIVERY_STATUS, WebhookDeliveryDialog} from '../../../Dialogs';
import {AppState} from '../../../AppState';

const DAYS = 14;
const PAGE_SIZE = 25;
const DELIVERED_COLOR = '#1f7a4c';
const FAILED_COLOR = '#c2410c';
const EVENTS = [...WEBHOOK_EVENTS].sort();

// Stats dates are UTC days, so don't convert them to local Dates
const dayLabel = (date: string) => {
  const [, month, day] = date.split('-');
  return `${Number(month)}/${Number(day)}`;
};

const count = (n: number) => n.toLocaleString();

export const WebhookDeliveryLog: FC = observer(() => {
  const organizationId = AppState.currentProfile?.organization_id;
  const [status, setStatus] = useState<'all' | TWebhookDeliveryStatus>('all');
  const [event, setEvent] = useState<'' | TWebhookEvent>('');
  const [envelopeInput, setEnvelopeInput] = useState('');
  const [envelopeId, setEnvelopeId] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<IWebhookDelivery | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const resendingRef = useRef(false);

  const statsQuery = useWebhookDeliveryStats(organizationId, DAYS);
  const deliveriesQuery = useWebhookDeliveries(organizationId, {
    rows: PAGE_SIZE,
    page,
    ...(status !== 'all' ? {status} : {}),
    ...(event ? {event} : {}),
    ...(envelopeId ? {envelope_id: envelopeId} : {}),
  });

  const applyEnvelope = (value: string) => {
    if (value !== envelopeId) {
      setEnvelopeId(value);
      setPage(0);
    }
  };

  useEffect(() => {
    const value = envelopeInput.trim();
    if (value === envelopeId) {
      return;
    }

    const timer = setTimeout(() => {
      setEnvelopeId(value);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [envelopeInput, envelopeId]);

  const filterEnvelope = (id: string) => {
    setSelected(null);
    setStatus('all');
    setEvent('');
    setEnvelopeInput(id);
    applyEnvelope(id);
  };

  const handleResend = async (delivery: IWebhookDelivery) => {
    // Tolerate double-clicks
    if (resendingRef.current) {
      return;
    }

    resendingRef.current = true;
    setResendingId(delivery.id);
    try {
      const result = await resendDelivery(organizationId, delivery.id);
      if (result.status === 'delivered') {
        toast.success('Delivery resent.');
      } else {
        toast.warning('The resend failed.');
      }
    } catch (e: any) {
      console.log('Error resending webhook delivery', e);
      toast.error(`Unable to resend the delivery: ${e.response?.data?.error || e.message}`);
    }
    resendingRef.current = false;
    setResendingId(null);
  };

  if (statsQuery.isError || deliveriesQuery.isError) {
    return (
      <Card>
        <div className="text-[13px] text-gray-500">Unable to load the delivery log. Please try again later.</div>
      </Card>
    );
  }

  if (!statsQuery.data || !deliveriesQuery.data) {
    return <Spinner mode="dark" />;
  }

  const stats = statsQuery.data;
  const {deliveries, count: total} = deliveriesQuery.data;
  const filtered = status !== 'all' || !!event || !!envelopeId;
  const delivered = stats.reduce((sum, day) => sum + day.delivered, 0);
  const failed = stats.reduce((sum, day) => sum + day.failed, 0);
  const successRate = delivered + failed > 0 ? (delivered / (delivered + failed)) * 100 : null;
  const first = page * PAGE_SIZE + 1;
  const last = Math.min((page + 1) * PAGE_SIZE, total);

  if (total === 0 && !filtered && delivered + failed === 0) {
    return <EmptyState title="No deliveries yet">Deliveries show up here as events are sent to your endpoint.</EmptyState>;
  }

  return (
    <>
      <div className="mb-4 grid grid-cols-3 gap-3">
        <StatTile label={`Delivered, ${DAYS} days`} value={count(delivered)} />
        <StatTile label="Failed" value={count(failed)} tone={failed > 0 ? 'warn' : 'default'} />
        <StatTile
          label="Success rate"
          value={successRate === null ? '-' : `${successRate.toFixed(1)}%`}
          tone={successRate === null ? 'default' : successRate < 95 ? 'warn' : 'ok'}
        />
      </div>

      <Card className="mb-4">
        <SectionTitle>Deliveries, past two weeks</SectionTitle>
        <LineChart
          labels={stats.map((day) => dayLabel(day.date))}
          series={[
            {key: 'delivered', label: 'Delivered', color: DELIVERED_COLOR, values: stats.map((day) => day.delivered)},
            {key: 'failed', label: 'Failed', color: FAILED_COLOR, values: stats.map((day) => day.failed)},
          ]}
        />
      </Card>

      <Card>
        <SectionTitle>Recent deliveries</SectionTitle>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <SegmentedControl
            value={status}
            onChange={(key) => {
              setStatus(key as 'all' | TWebhookDeliveryStatus);
              setPage(0);
            }}
            options={[
              {key: 'all', label: 'All'},
              {key: 'delivered', label: 'Delivered'},
              {key: 'failed', label: 'Failed'},
              {key: 'pending', label: 'Pending'},
            ]}
          />
          <select
            aria-label="Event"
            value={event}
            onChange={(e) => {
              setEvent(e.target.value as '' | TWebhookEvent);
              setPage(0);
            }}
            className={twMerge(FIELD_CLASSES, 'w-auto pr-8')}>
            <option value="">All events</option>
            {EVENTS.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <input
            type="search"
            aria-label="Envelope ID"
            placeholder="Envelope ID"
            value={envelopeInput}
            onChange={(e) => setEnvelopeInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyEnvelope(envelopeInput.trim())}
            className={twMerge(FIELD_CLASSES, 'w-full font-mono text-[12px] tablet:w-[300px]')}
          />
        </div>

        {deliveries.length === 0 ? (
          <div className="py-6 text-center text-[13px] text-gray-500">Nothing matches these filters.</div>
        ) : (
          <div className={`overflow-x-auto transition-opacity ${deliveriesQuery.isPlaceholderData ? 'opacity-60' : ''}`}>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-[#ebecf0] text-[11px] uppercase tracking-wide text-gray-400">
                  <th className="hidden py-1.5 text-left font-medium tablet:table-cell">Time</th>
                  <th className="py-1.5 text-left font-medium tablet:pl-3">Event</th>
                  <th className="py-1.5 pl-3 text-left font-medium">Status</th>
                  <th className="hidden py-1.5 pl-3 text-right font-medium tablet:table-cell">Attempts</th>
                  <th className="py-1.5 pl-3" />
                </tr>
              </thead>
              <tbody>
                {deliveries.map((delivery) => (
                  <tr
                    key={delivery.id}
                    tabIndex={0}
                    onClick={() => setSelected(delivery)}
                    onKeyDown={(e) => e.key === 'Enter' && setSelected(delivery)}
                    className="cursor-pointer border-b border-[#f2f3f5] outline-none last:border-0 hover:bg-gray-50 focus-visible:bg-gray-50">
                    <td
                      className="hidden whitespace-nowrap py-2 tabular-nums text-gray-500 tablet:table-cell"
                      title={new Date(delivery.created_at).toLocaleString()}>
                      {format(new Date(delivery.created_at), 'MMM d, h:mm a')}
                    </td>
                    <td className="py-2 tablet:pl-3">
                      <div className="font-mono text-[12px] text-[#33364b]">{delivery.event}</div>
                      <div className="text-[12px] tabular-nums text-gray-500 tablet:hidden">
                        {format(new Date(delivery.created_at), 'MMM d, h:mm a')}
                      </div>
                    </td>
                    <td className="py-2 pl-3">
                      <Badge tone={DELIVERY_STATUS[delivery.status].tone}>
                        {DELIVERY_STATUS[delivery.status].label}
                        {delivery.last_status ? ` ${delivery.last_status}` : ''}
                      </Badge>
                    </td>
                    <td className="hidden py-2 pl-3 text-right tabular-nums text-gray-500 tablet:table-cell">{delivery.attempts}</td>
                    <td className="py-1 pl-3 text-right" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      {delivery.status === 'failed' && (
                        <RowAction title="Resend" disabled={resendingId === delivery.id} onClick={() => handleResend(delivery)}>
                          <ArrowPathIcon />
                        </RowAction>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="mt-3 flex items-center justify-between gap-3 border-t border-[#f2f3f5] pt-3">
            <div className="text-[12px] tabular-nums text-gray-500">
              {count(first)}-{count(last)} of {count(total)}
            </div>
            <div className="flex gap-2">
              <Button size="xsmall" variant="secondary" label="Previous" disabled={page === 0} onClick={() => setPage(page - 1)} />
              <Button size="xsmall" variant="secondary" label="Next" disabled={last >= total} onClick={() => setPage(page + 1)} />
            </div>
          </div>
        )}
      </Card>

      {selected && (
        <WebhookDeliveryDialog
          organizationId={organizationId}
          delivery={selected}
          resending={resendingId === selected.id}
          onResend={() => handleResend(selected)}
          onFilterEnvelope={filterEnvelope}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
});
